import {selectEquipmentRunningTimes, usePlantsRunningTimes} from '@domain/running-times'
import {ShiftEventAndUpdateEvent, useRequiredConfig, BaseEvent} from '@domain/shifthandover'
import {MAX_DESCRIPTION_CHARACTER_LIMIT} from '@hconnect/common/consts'
import {DateRange, DisabledFields, EquipmentIdType, Stoppage} from '@hconnect/common/types'
import {removeMarkdownTags, getQuickSelectFilter, toDateRange} from '@hconnect/common/utils'
import {
  shouldNotBeEmpty,
  shouldNotBeEmptyString,
  shouldNotBeEmptyStringWithMaxLength,
  ValidatorConfig
} from '@hconnect/common/validators'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {keepPreviousData} from '@tanstack/react-query'
import {StoppageFormFields} from '@widgets/common-shift-event'
import {TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'
import React, {useState} from 'react'

import {
  requiredEquipmentNumberValidator,
  requiredStoppedMainEquipmentNumberValidator,
  stoppageCodeValidator,
  stoppageReasonValidator,
  validFromValidatorWithOverlapCheck,
  validToValidatorWithOverlapCheck
} from '../../validators/shiftEvent.validators'
import {EventInfo} from '../eventProperties/EventInfo'

type TypeOfStoppage = Stoppage
type PossibleErrors = keyof TypeOfStoppage | 'stoppageCode' | 'stoppageReason' | 'equipmentNumber'

export type Props = {
  item: Partial<TypeOfStoppage>
  updateMultiPart: (delta) => void
  validationError: Map<PossibleErrors, string>
  children: React.ReactNode
  isFunctionalLocationRequired?: boolean
  hideStoppageDateType?: boolean
  disabledFields: DisabledFields
  equipmentIdType?: EquipmentIdType
  autocompleteProcessStage?: boolean
  plantId: string
  isEquipmentAvailable?: boolean
}

// As the UI has to reflect the visible fields and knows best which of them is Required,
// the Validator config is put here to have both aligned at all time.

export const stoppageValidatorConfig = (
  t: TFunction,
  isFunctionalLocationRequired?: boolean,
  existingEvent?: BaseEvent,
  skipOverlapValidation?: boolean
): ValidatorConfig<ShiftEventAndUpdateEvent> => {
  const commonValidators = new Map([
    ['title', shouldNotBeEmptyString<ShiftEventAndUpdateEvent>()],
    ['eventType', shouldNotBeEmpty<ShiftEventAndUpdateEvent>()],
    ['stoppageCode', stoppageCodeValidator],
    ['stoppageReason', stoppageReasonValidator],
    ['stoppageStart', validFromValidatorWithOverlapCheck(t, existingEvent, skipOverlapValidation)],
    ['stoppageEnd', validToValidatorWithOverlapCheck(t, existingEvent, skipOverlapValidation)],
    [
      'description',
      shouldNotBeEmptyStringWithMaxLength<ShiftEventAndUpdateEvent>(
        MAX_DESCRIPTION_CHARACTER_LIMIT,
        removeMarkdownTags
      )
    ],
    ['stoppedMainEquipmentNumbers', requiredStoppedMainEquipmentNumberValidator]
  ])
  if (isFunctionalLocationRequired) {
    commonValidators.set('equipmentNumber', requiredEquipmentNumberValidator)
  }
  return commonValidators
}

/**
 * if you consider using this component, check first src/container/EventFormContainer.tsx,
 * as it handles the validation and the stuff around this form,
 * it might already solve all your needs
 */
export const StoppageForm: React.FC<Props> = ({
  children,
  item,
  updateMultiPart,
  validationError,
  hideStoppageDateType,
  disabledFields,
  equipmentIdType,
  autocompleteProcessStage,
  plantId,
  isFunctionalLocationRequired
}) => {
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const {
    shifts,
    kilnStoppageCodes,
    rawMillStoppageCodes,
    finishMillStoppageCodes,
    alternativeFuelInstallationStoppageCodes,
    workOrderTarget
  } = useRequiredConfig(plantId)
  const dateRange = getQuickSelectFilter('currentShift', shifts, getPlantNow())
  const [start, end] = dateRange
  const [range, setRange] = useState<[start: Moment, end: Moment]>(
    start && end ? [start, end] : [moment().subtract(1, 'day'), moment()]
  )
  const timeRange: DateRange = toDateRange(range)

  const {data = [], isFetching} = usePlantsRunningTimes(
    {
      plantIds: [plantId],
      from: timeRange.startDate.toISOString(),
      to: timeRange.endDate.toISOString()
    },
    {
      enabled: !hideStoppageDateType,
      placeholderData: keepPreviousData,
      select: selectEquipmentRunningTimes(timezone)
    }
  )

  const loadMoreRunningTimes = () => {
    setRange([range[0].subtract(1, 'day'), range[1]])
  }

  const runningTimesData = !hideStoppageDateType
    ? {
        data,
        isLoading: isFetching,
        fetchMore: loadMoreRunningTimes
      }
    : undefined

  return (
    <>
      {'created' in item && (
        <EventInfo
          item={item as Partial<Stoppage>}
          workOrderTarget={workOrderTarget}
          plantId={plantId}
        />
      )}

      <StoppageFormFields
        item={item}
        updateMultiPart={updateMultiPart}
        validationError={validationError}
        timezone={timezone}
        plantId={plantId}
        runningTimesData={runningTimesData}
        stoppageCodes={{
          kilnStoppageCodes: kilnStoppageCodes,
          rawMillStoppageCodes: rawMillStoppageCodes,
          finishMillStoppageCodes: finishMillStoppageCodes,
          alternativeFuelInstallationStoppageCodes: alternativeFuelInstallationStoppageCodes
        }}
        hiddenFields={{stoppageType: true, hasSalesImpact: true}}
        disabledFields={disabledFields}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
        isFunctionalLocationRequired={isFunctionalLocationRequired}
      >
        {children}
      </StoppageFormFields>
    </>
  )
}
